import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface HubspotSubmissionResult {
	success: boolean;
	message?: string;
}

interface HubspotFormField {
	objectTypeId: string;
	name: string;
	value: string;
}

interface HubspotFormPayload {
	fields: HubspotFormField[];
	context: {
		pageUri?: string;
		pageName?: string;
	};
}

@Injectable({
	providedIn: 'root',
})
export class HubspotService {
	private readonly http = inject(HttpClient);
	private readonly hubspotApiUrl = 'https://api.hsforms.com/submissions/v3/integration/submit';

	/**
	 * Submits an email to a HubSpot form
	 * @param portalId - HubSpot Portal ID
	 * @param formId - HubSpot Form GUID
	 * @param email - Email address to submit
	 * @returns Observable with submission result
	 */
	submitForm(portalId: string, formId: string, email: string): Observable<HubspotSubmissionResult> {
		const url = `${this.hubspotApiUrl}/${portalId}/${formId}`;

		const payload: HubspotFormPayload = {
			fields: [
				{
					objectTypeId: '0-1',
					name: 'email',
					value: email,
				},
			],
			context: {
				pageUri: typeof window !== 'undefined' ? window.location.href : '',
				pageName: typeof document !== 'undefined' ? document.title : '',
			},
		};

		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		return this.http.post(url, payload, { headers }).pipe(
			map(() => ({
				success: true,
				message: 'Successfully subscribed!',
			})),
			catchError((error) => {
				console.error('HubSpot form submission error:', error);

				let errorMessage = 'An error occurred during submission.';

				if (error.status === 400) {
					errorMessage = 'Invalid email address or form data.';
				} else if (error.status === 404) {
					errorMessage = 'Form not found. Please contact support.';
				} else if (error.status === 429) {
					errorMessage = 'Too many requests. Please try again later.';
				}

				return of({
					success: false,
					message: errorMessage,
				});
			})
		);
	}
}
